// ==========================================
// UPDATED AuthForm.js - With Anonymous/Guest Login
// ==========================================
import React, { useState } from 'react';
import { X, LogIn, UserPlus, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { API_URL } from '../../../utils/constants';
import { encryptPassword } from '../../../utils/helpers';

// US States list
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri',
  'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island',
  'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia',
  'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const AuthForm = () => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    bio: '',
    interests: [],
    country: 'United States',
    state: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, loginAnonymous } = useAuth();

  const handleInterestToggle = (interest) => {
    const interests = formData.interests.includes(interest)
      ? formData.interests.filter(i => i !== interest)
      : [...formData.interests, interest];
    setFormData({ ...formData, interests });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const encryptedPassword = await encryptPassword(formData.password);

      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const data = isLogin
        ? { username: formData.username, password: encryptedPassword }
        : { ...formData, password: encryptedPassword };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Authentication failed' }));
        throw new Error(errorData.detail || errorData.error || 'Authentication failed');
      }

      const result = await response.json();
      login(result);
      setShowLoginModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = () => {
    loginAnonymous();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
      {!showLoginModal ? (
        // Welcome Screen
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 max-w-md w-full text-center">
          <div className="text-6xl mb-4">🐾</div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
            Welcome to PetConnectAI
          </h1>
          <p className="text-gray-500 text-sm mb-8">
            Connect with pet lovers, find lost pets, and help animals in need
          </p>

          <div className="space-y-3">
            {/* Primary: Login */}
            <button
              onClick={() => { setIsLogin(true); setShowLoginModal(true); }}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition font-semibold shadow-md"
            >
              <LogIn size={20} />
              Login
            </button>

            {/* Secondary: Sign Up */}
            <button
              onClick={() => { setIsLogin(false); setShowLoginModal(true); }}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 border-2 border-purple-600 text-purple-600 rounded-xl hover:bg-purple-50 transition font-semibold"
            >
              <UserPlus size={20} />
              Create Account
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 my-2">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 font-medium">OR</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Guest / Anonymous Login */}
            <button
              onClick={handleGuestLogin}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-semibold border border-gray-300"
            >
              <span className="text-lg">👤</span>
              Continue as Guest
            </button>

            <p className="text-xs text-gray-400 mt-1">
              Browse & search without an account. Some features require login.
            </p>

            {/* Back to Search */}
            <button
              onClick={() => window.location.href = '/'}
              className="w-full flex items-center justify-center gap-2 px-6 py-2 text-gray-500 hover:text-gray-700 transition text-sm"
            >
              ← Back to Search
            </button>
          </div>

          {/* Feature hints */}
          <div className="mt-6 pt-6 border-t border-gray-100 text-left space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Guest users can:</p>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span className="text-green-500">✓</span> Search for lost & found pets
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span className="text-green-500">✓</span> Browse the community feed
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span className="text-green-500">✓</span> View pet walkers & adoption listings
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span className="text-orange-400">~</span> Creating posts requires an account
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span className="text-orange-400">~</span> Messaging requires an account
            </div>
          </div>
        </div>
      ) : (
        // Login/Signup Modal
        <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 w-full max-w-md relative max-h-[90vh] overflow-y-auto">
          <button
            onClick={() => window.location.href = '/'}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition p-1"
            title="Back"
          >
            <X size={24} />
          </button>

          <div className="text-center mb-6 pr-8">
            <div className="text-4xl mb-2">🐾</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-1">
              {isLogin ? 'Welcome Back' : 'Join PetConnectAI'}
            </h2>
            <p className="text-sm text-gray-500">
              {isLogin ? 'Login to your account' : 'Create a free account'}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              placeholder="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm"
              required
            />

            {!isLogin && (
              <>
                <input
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                  required
                />

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Country</label>
                  <select
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value, state: '' })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                    required
                  >
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Australia">Australia</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {formData.country === 'United States' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      State <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                      required
                    >
                      <option value="">Select State</option>
                      {US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                )}

                <textarea
                  placeholder="Tell us about yourself (optional)"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm"
                  rows={2}
                />

                <div className="border border-gray-200 rounded-xl p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Your Interests:</p>
                  <div className="flex flex-wrap gap-2">
                    {['cats', 'dogs', 'adoption', 'missing'].map(interest => (
                      <button
                        key={interest}
                        type="button"
                        onClick={() => handleInterestToggle(interest)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                          formData.interests.includes(interest)
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {interest.charAt(0).toUpperCase() + interest.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-xl hover:from-purple-700 hover:to-pink-700 transition font-semibold disabled:opacity-50 text-sm shadow-md"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Create Account')}
            </button>
          </form>

          {/* Divider + Guest Option */}
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400">OR</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
            <button
              onClick={() => { loginAnonymous(); }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-50 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-100 transition font-medium text-sm"
            >
              <span>👤</span>
              Continue as Guest (no account needed)
            </button>
          </div>

          <p className="text-center mt-4 text-sm text-gray-500">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setFormData({ username: '', email: '', password: '', bio: '', interests: [], country: 'United States', state: '' });
              }}
              className="text-purple-600 hover:underline font-semibold"
            >
              {isLogin ? 'Sign Up' : 'Login'}
            </button>
          </p>
        </div>
      )}
    </div>
  );
};

export default AuthForm;